import { NavArrowDown } from 'iconoir-react'
import classNames from 'classnames'
import { useState } from 'react'
import { StatsCards } from './StatsCards'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { StatsCardType } from 'types/StatsCardType'

export default function SummaryTab({
  title,
  graph,
  statsCards,
  defaultChartOpen = false,
  hideChartOnMobile = false,
  mobileReplacement,
  graphBorderless = false,
}: {
  title?: string
  graph: React.ReactElement
  statsCards: StatsCardType[]
  /** Mobile only: whether the chart starts expanded. Defaults to collapsed so
   *  the page leads with the numbers; the funnel section opts in to open. */
  defaultChartOpen?: boolean
  /** Mobile only: drop the chart entirely. Time-series graphs read poorly on a
   *  phone and the stat cards already carry the totals + trend deltas, so the
   *  Leads/Schedule sections omit their chart on mobile. */
  hideChartOnMobile?: boolean
  /** Mobile only: rendered in place of the dropped chart (e.g. a compact trend
   *  list). Shown when the chart is dropped in favor of the stat cards. */
  mobileReplacement?: React.ReactElement
  /** Desktop: render the graph without the border/padding wrapper. Use when the
   *  graph component has its own chrome or the section should feel flush. */
  graphBorderless?: boolean
}) {
  const isMobile = useIsMobile()
  // Only drop the chart when there are stat cards to show in its place. If a page
  // has no cards for the current data (e.g. sparse New Patients), keep the chart
  // so the tab is never blank — and start it open since it's the only content.
  const hasStats = statsCards.length > 0
  const dropChart = isMobile && hideChartOnMobile && hasStats
  const [chartOpen, setChartOpen] = useState(defaultChartOpen || (hideChartOnMobile && !hasStats))

  return (
    <div x-component-name="SummaryTab" className="mx-auto w-full">
      <StatsCards title={title} stats={statsCards} />
      {dropChart ? (
        mobileReplacement ? (
          <div className="mt-2">{mobileReplacement}</div>
        ) : null
      ) : isMobile ? (
        // Numbers-first on mobile: the chart is an accordion under the KPIs. The
        // graph is MOUNTED ON OPEN (not display:none-hidden) — visx/ParentSize
        // measures 0 while hidden and won't recover on reveal, so a kept-mounted
        // chart renders blank. The graphs are presentational (data lives in the
        // parent section), so mounting fresh on expand is safe.
        <div className="rounded-lg border border-default">
          <button
            type="button"
            onClick={() => setChartOpen((open) => !open)}
            aria-expanded={chartOpen}
            className="flex w-full items-center justify-between px-4 py-3 text-body-sm font-medium text-default"
          >
            <span>Chart</span>
            <NavArrowDown
              className={classNames(`h-4 w-4 text-minimal transition-transform`, chartOpen && `rotate-180`)}
              strokeWidth={2}
            />
          </button>
          {chartOpen && <div className="border-t border-default p-3">{graph}</div>}
        </div>
      ) : // Desktop-only branch (isMobile is false here), so these are the original
      // unprefixed classes. min-w-[800px] must stay ungated: gating it to lg
      // dropped the 800px floor in the 768-1023px band, which desktop had.
      graphBorderless ? (
        <div className="min-w-[800px]">{graph}</div>
      ) : (
        <div className="min-w-[800px] rounded-lg border border-default p-6">{graph}</div>
      )}
    </div>
  )
}
